'use client';

import { forwardRef } from 'react';

import { cn } from '../utils/cn';

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  containerClassName?: string;
}

/**
 * Display simple, static tabular data. For more complex data tables, use `DataTable`.
 * @see {@link https://stylus.design/table}
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, containerClassName, ...props }, ref) => (
    <div className={cn('relative w-full overflow-auto', containerClassName)}>
      <table
        ref={ref}
        className={cn(
          'w-full caption-bottom border-collapse bg-white text-sm text-black tabular-nums [&_*>time]:normal-nums',
          className,
        )}
        {...props}
      />
    </div>
  ),
);

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      'bg-white [&_tr]:border-b [&_tr]:border-slate-200 [&_tr]:hover:bg-white',
      className,
    )}
    {...props}
  />
));

export const TableCell = forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    className={cn(
      'min-w-[68px] p-4 text-sm font-normal leading-tight text-slate-700 [&:has([role=checkbox])]:min-w-min [&:has([role=checkbox])]:min-w-min [&:has([role=checkbox])]:p-3 [&:has([role=checkbox])]:pl-2',
      className,
    )}
    {...props}
  />
));

interface TableSkeletonProps {
  numColumns?: number;
  numRows?: number;
  checkboxRow?: number;
  className?: string;
}

export const TableSkeleton = forwardRef<HTMLTableRowElement, TableSkeletonProps>(
  ({ checkboxRow = -1, numColumns = 1, numRows = 5, className, ...props }, ref) => {
    const content = Array.from({ length: numRows }).map((_row, rowIndex) => (
      // eslint-disable-next-line react/no-array-index-key
      <tr data-testid='skeleton-row' ref={ref} key={`skeleton-row-${rowIndex}`} {...props}>
        {Array.from({ length: numColumns }).map((_col, colIndex) => {
          const rowOpacity = Math.max(0, 100 - rowIndex * 10);

          return (
            <TableCell key={`row-${rowIndex.toString()}-col-${colIndex.toString()}`}>
              <div
                {...(checkboxRow === colIndex ? { role: 'checkbox' } : {})}
                className={cn(
                  'animate-bg-pulse h-5 w-full rounded',
                  {
                    'w-[20px]': checkboxRow === colIndex,
                  },
                  className,
                )}
                style={{
                  opacity: `${rowOpacity}%`,
                }}
              />
            </TableCell>
          );
        })}
      </tr>
    ));

    return <tbody>{content}</tbody>;
  },
);

export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {
  loading?: boolean;
  numColumns?: number;
  numRows?: number;
  skeletonClassName?: string;
  checkboxRow?: number;
}

export const TableBody = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  (
    { className, skeletonClassName, loading, numColumns, numRows, children, checkboxRow, ...props },
    ref,
  ) =>
    loading ? (
      <TableSkeleton
        numColumns={numColumns}
        numRows={numRows}
        className={skeletonClassName}
        checkboxRow={checkboxRow}
      />
    ) : (
      <tbody ref={ref} className={cn('bg-white [&_tr:last-child]:border-0', className)} {...props}>
        {children}
      </tbody>
    ),
);

export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      'border-t border-slate-200 bg-white font-medium [&>tr]:last:border-b-0',
      className,
    )}
    {...props}
  />
));

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  disabled?: boolean;
}

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, disabled, ...props }, ref) => (
    <tr
      ref={ref}
      className={cn(
        'data-[state=selected]:bg-light-selected border-b border-slate-100 transition-colors hover:bg-slate-50',
        className,
        {
          'opacity-30': disabled,
        },
      )}
      {...props}
    />
  ),
);

export const TableHead = forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, children, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      'px-4 py-3 text-left text-sm font-semibold leading-tight text-slate-900 [&:has([role=checkbox])]:min-w-min [&:has([role=checkbox])]:p-3 [&:has([role=checkbox])]:pr-0',
      !children ? 'min-w-min' : 'min-w-[68px]',
      className,
    )}
    {...props}
  >
    <div className='inline-flex items-center justify-start'>{children}</div>
  </th>
));

export const TableCaption = forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption ref={ref} className={cn('text-muted-foreground mt-4 text-xs', className)} {...props} />
));
